// Office Subcontractors directory (Admin nav). Lists the standing subcontractors, with
// status + agreement badges, filters, and an "Add Subcontractor" modal that triggers the
// onboarding invite. Row click opens the detail page.
//
// SESSION AUV, design system Session 8 (People): the page now speaks the shared design system. PageHeader, ListToolbar
// (the existing search box MOVED onto it: the term still goes to the server on every change exactly as before and the
// status and type chip rows are server filters too, so no client search and no result count were added; Clear resets
// all three; plus the below-md sort select), sortable headers over the rows the server returned, StatusPill tags,
// Button, Loader, EmptyState and lib/format dates (Updated prints "5 Oct 2026" where it printed "05/10/2026").
// Presentation only: every route, call, permission and word of copy is as before.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import type { InviteNavState, StandardCardVersion, SubcontractorCreateInput, SubcontractorListItem } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { londonToday, rateBodyFromForm, rateFormFrom, type RateFiguresForm } from "../../lib/rateCardUi";
import { KindCards, LabourOptions, SUPPLY_AND_FIT_NOTE, type TypeChoiceValue } from "./SubcontractorTypeChoice";
import { RateCardTable, RateFiguresFields } from "./RateCardFigures";
import { StatusTag, AgreementTag, SubcontractorArchiveDialog, SubcontractorDeleteDialog } from "./subcontractorsUi";
import { Button, ButtonLink } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { ListToolbar } from "../../components/ListToolbar";
import { Loader } from "../../components/Loader";
import { PageHeader } from "../../components/PageHeader";
import { SortableHeader } from "../../components/SortableHeader";
import { StatusPill } from "../../components/StatusPill";
import { formatDate } from "../../lib/format";
import { useListSearch } from "../../lib/useListSearch";
import { sortRows, useSortableColumn, type SortAccessors } from "../../lib/useSortableColumn";
import { statusEntry } from "../../lib/statusRegistry";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";

// The "archived" value is not a Subcontractor status: it switches the list to the archived
// view (?archived=archived). Every other value filters the active (non-archived) directory.
const STATUS_FILTERS: Array<{ value: string; label: string }> = [
  { value: "", label: "All" },
  { value: "pending_invite", label: "Awaiting registration" },
  { value: "pending_review", label: "Pending review" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "archived", label: "Archived" },
];

// Session AUR: the Type filter and column read the office-owned STATUS. "Pending approval" is Bona Fide recorded by the
// sign-up form that nobody in the office has confirmed; "Not set" is every subcontractor still to be classified.
const TYPE_FILTERS: Array<{ value: string; label: string }> = [
  { value: "", label: "All types" },
  { value: "bona_fide", label: "Bona Fide" },
  { value: "pending", label: "Bona Fide, pending approval" },
  { value: "labour_only", label: "Labour Only" },
  { value: "not_set", label: "Not set" },
];

// Sort runs client-side over the rows the server returned for the status, type and search filters.
const SUB_SORT: SortAccessors<SubcontractorListItem> = {
  company: (r) => r.company,
  contact: (r) => r.contactName,
  trade: (r) => r.trade,
  type: (r) => statusEntry("subcontractorEngagement", r.subcontractorStatus ?? "not_set").label,
  status: (r) => r.status,
  agreement: (r) => r.agreementStatus,
  docs: (r) => r.docCount,
  updated: (r) => r.updatedAt,
};
const SORT_OPTIONS = [
  { key: "company", label: "Company" },
  { key: "trade", label: "Trade" },
  { key: "status", label: "Status" },
  { key: "updated", label: "Updated" },
];

export function SubcontractorsPage() {
  const navigate = useNavigate();
  // Card list below md, table otherwise -- decided once at mount (the InvoicesPage mechanism).
  // jsdom has no matchMedia, so every existing test still renders the table it pins.
  const [mobileCards] = useState(initialMobileLayout);
  const [rows, setRows] = useState<SubcontractorListItem[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  // The search term goes to the server (api.subcontractors.list), as it always did; useListSearch only binds it to ?q=.
  const { search, setSearch } = useListSearch();
  const sort = useSortableColumn("subcontractors");
  const [adding, setAdding] = useState(false);

  const archivedView = status === "archived";

  const load = useCallback(() => {
    setLoadError(false);
    const common = { type: type || undefined, search: search.trim() || undefined };
    const params =
      status === "archived"
        ? { ...common, archived: "archived" as const }
        : { ...common, status: status || undefined };
    api.subcontractors
      .list(params)
      .then(setRows)
      .catch(() => setLoadError(true));
  }, [status, type, search]);

  useEffect(() => {
    load();
  }, [load]);

  const sorted = useMemo(() => sortRows(rows ?? [], SUB_SORT, sort), [rows, sort.sortKey, sort.sortDir]);
  const trades = useMemo(() => Array.from(new Set((rows ?? []).map((r) => r.trade).filter(Boolean))).sort(), [rows]);

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-6">
      <PageHeader
        className="mb-4"
        title="Subcontractors"
        subtitle="The standing subcontractors ELE engages, their compliance and agreement status."
        actions={
          <>
            <ButtonLink to="/office/subcontractors/documents" variant="secondary">
              Document review
            </ButtonLink>
            <Button icon={<Plus aria-hidden size={15} />} onClick={() => setAdding(true)}>
              Add Subcontractor
            </Button>
          </>
        }
      />

      <ListToolbar
        className="mb-3"
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search company, contact, trade"
        searchLabel="Search subcontractors"
        sort={{ state: sort, options: SORT_OPTIONS, onChange: (st) => sort.setSort(st.sortKey, st.sortDir) }}
        onClear={() => {
          setSearch("");
          setStatus("");
          setType("");
        }}
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value || "all"}
            onClick={() => setStatus(f.value)}
            className={`rounded-ele border px-3 py-1.5 font-menu text-xs tracking-wide transition max-sm:min-h-11 ${
              status === f.value ? "border-ele-green bg-ele-inset text-ele-green" : "border-ele-purple-accent text-ele-body hover:text-ele-green"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="font-menu text-[10px] uppercase tracking-wide text-ele-body opacity-70">Type:</span>
        {TYPE_FILTERS.map((f) => (
          <button
            key={f.value || "all-types"}
            onClick={() => setType(f.value)}
            className={`rounded-ele border px-3 py-1.5 font-menu text-xs tracking-wide transition max-sm:min-h-11 ${
              type === f.value ? "border-ele-green bg-ele-inset text-ele-green" : "border-ele-purple-accent text-ele-body hover:text-ele-green"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loadError ? (
        <p role="alert" className="rounded-ele border border-ele-danger bg-ele-surface p-6 text-center text-ele-strong">
          Could not load subcontractors. Please try again.
        </p>
      ) : rows === null ? (
        <Loader label="Loading the subcontractors..." />
      ) : rows.length === 0 ? (
        <EmptyState message={archivedView ? "No archived subcontractors." : "No subcontractors yet."} />
      ) : mobileCards ? (
        /* Company, Type and Status are the surviving desktop-priority columns (Contact, Trade,
           Agreement, Docs, Updated and Actions are already max-md:hidden below), so the card keeps
           that shape: the company name never truncates, the two badges come back into the right
           rail, and the remaining fields move into details where each gets its own full line. The
           whole card taps through to the detail page exactly as the row click already does, so
           Archive/Delete (or Restore) sit in actions, a sibling of the link rather than nested in it. */
        <MobileCardList label="Subcontractors">
          {sorted.map((r) => {
            const muted = Boolean(r.archivedAt);
            const mutedCls = muted ? "text-ele-faint" : undefined;
            return (
              <MobileListCard
                key={r.id}
                to={`/office/subcontractors/${r.id}`}
                primary={<span className={mutedCls}>{r.company}</span>}
                secondary={<span className={mutedCls}>{r.contactName}</span>}
                meta={<span className={mutedCls}>{r.trade || "-"}</span>}
                right={
                  <>
                    <StatusPill domain="subcontractorEngagement" status={r.subcontractorStatus ?? "not_set"} />
                    <StatusTag status={r.status} />
                  </>
                }
                details={
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                    <dt className="text-ele-body">Agreement</dt>
                    <dd><AgreementTag status={r.agreementStatus} /></dd>
                    <dt className="text-ele-body">Documents</dt>
                    <dd className="text-ele-body">{r.docCount}</dd>
                    <dt className="text-ele-body">Updated</dt>
                    <dd className="text-ele-body">{formatDate(r.updatedAt)}</dd>
                  </dl>
                }
                actions={muted ? <RowRestoreButton sub={r} onRestored={load} /> : <RowActions sub={r} onChanged={load} />}
              />
            );
          })}
        </MobileCardList>
      ) : (
        <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
          <table className="ele-list-table w-full text-left text-sm">
            <colgroup>
              <col style={{ width: "20%" }} />
              <col className="max-md:hidden" style={{ width: "16%" }} />
              <col className="max-md:hidden" style={{ width: "12%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "10%" }} />
              <col className="max-md:hidden" style={{ width: "11%" }} />
              <col className="max-md:hidden" style={{ width: "6%" }} />
              <col className="max-md:hidden" style={{ width: "9%" }} />
              {/* The Actions column (Archive/Delete or Restore) is hidden below md: every row
                  navigates to the detail page, which carries the full Archive/Restore/Delete
                  controls, so nothing is lost by tapping through on a small screen. */}
              <col className="max-md:hidden" style={{ width: "8%" }} />
              <col className="w-8 md:hidden" />
            </colgroup>
            <thead className="bg-ele-inset font-menu text-xs uppercase tracking-wide text-ele-blue">
              <tr>
                <SortableHeader label="Company" colKey="company" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Contact" colKey="contact" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Trade" colKey="trade" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Type" colKey="type" sort={sort} onToggle={sort.toggle} className="cell-actions" />
                <SortableHeader label="Status" colKey="status" sort={sort} onToggle={sort.toggle} className="cell-actions" />
                <SortableHeader label="Agreement" colKey="agreement" sort={sort} onToggle={sort.toggle} className="max-md:hidden cell-actions" />
                <SortableHeader label="Docs" colKey="docs" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Updated" colKey="updated" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <th className="max-md:hidden cell-actions px-3 py-2 text-right">Actions</th>
                <th className="w-8 cell-actions md:hidden" aria-hidden="true" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => {
                // Archived rows are de-emphasised (muted grey) and offer only Restore.
                const muted = Boolean(r.archivedAt);
                return (
                  <tr
                    key={r.id}
                    onClick={() => navigate(`/office/subcontractors/${r.id}`)}
                    className="group cursor-pointer border-t border-ele-purple-accent/60 hover:bg-ele-row-hover"
                  >
                    <td className={`px-3 py-2 font-semibold ${muted ? "text-ele-faint" : "text-ele-strong"}`}>{r.company}</td>
                    <td className={`max-md:hidden px-3 py-2 ${muted ? "text-ele-faint" : "text-ele-body"}`}>{r.contactName}</td>
                    <td className={`max-md:hidden px-3 py-2 ${muted ? "text-ele-faint" : "text-ele-body"}`}>{r.trade || "-"}</td>
                    <td className="cell-actions px-3 py-2"><StatusPill domain="subcontractorEngagement" status={r.subcontractorStatus ?? "not_set"} /></td>
                    <td className="cell-actions px-3 py-2"><StatusTag status={r.status} /></td>
                    <td className="max-md:hidden cell-actions px-3 py-2"><AgreementTag status={r.agreementStatus} /></td>
                    <td className={`max-md:hidden px-3 py-2 ${muted ? "text-ele-faint" : "text-ele-body"}`}>{r.docCount}</td>
                    <td className={`max-md:hidden px-3 py-2 ${muted ? "text-ele-faint" : "text-ele-body"}`}>{formatDate(r.updatedAt)}</td>
                    <td className="max-md:hidden cell-actions px-3 py-2 text-right">
                      {muted ? <RowRestoreButton sub={r} onRestored={load} /> : <RowActions sub={r} onChanged={load} />}
                    </td>
                    <td className="cell-actions md:hidden pr-1 text-right">
                      <svg aria-hidden viewBox="0 0 16 16" className="ml-auto h-4 w-4 fill-current text-ele-purple transition-colors group-hover:text-ele-green group-focus-within:text-ele-green">
                        <path d="M6 4l4 4-4 4z" />
                      </svg>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {trades.length > 0 && (
        <p className="mt-3 text-xs text-ele-body opacity-60">Trades on file: {trades.join(", ")}.</p>
      )}

      {adding && (
        <AddModal
          onClose={() => setAdding(false)}
          onAdded={(id, invite) => {
            setAdding(false);
            // Session ADU: the modal navigates straight to the detail page on success, so the invite's outcome cannot
            // live in the modal (it would flash and vanish). Session RC2: carry it to the detail page, which says the
            // invite is waiting in Gmail Drafts (and until when its link works), or why not, pointing at the link.
            navigate(`/office/subcontractors/${id}`, { state: { invite } });
          }}
        />
      )}
    </div>
  );
}

// Session RC1: the type comes first, as two large cards; a Labour subcontractor then picks the Standard card (the default)
// or Custom rates (an admin only), the Technician tick and the date the rates start. The save button's words ARE the
// server's own preview of what will happen ("Create subcontractor on Standard card v1 from 23 Oct 2026"), asked again
// whenever the choice changes, so the office reads exactly what the press will do before pressing it.
function AddModal({ onClose, onAdded }: { onClose: () => void; onAdded: (id: string, invite: InviteNavState) => void }) {
  const isAdmin = useAuth().user?.role === "admin";
  const today = useMemo(() => londonToday(), []);
  const [form, setForm] = useState({ company: "", contactName: "", email: "", trade: "", contactPhone: "" });
  const [type, setType] = useState<TypeChoiceValue>({ kind: null, rateCardSource: "STANDARD", technician: false, effectiveFrom: today });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [plan, setPlan] = useState<string | null>(null);
  const [planErr, setPlanErr] = useState<string | null>(null);
  // Admin only: the standard card in force, read-only for Standard, and the prefill for Custom rates.
  const [standard, setStandard] = useState<StandardCardVersion | null>(null);
  const [custom, setCustom] = useState<RateFiguresForm | null>(null);

  function set<K extends keyof typeof form>(k: K, v: string) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  useEffect(() => {
    if (!isAdmin) return;
    let live = true;
    api.standardRateCard
      .get()
      .then((v) => {
        if (!live) return;
        const cur = v.versions.find((x) => x.id === v.currentVersionId) ?? null;
        setStandard(cur);
        setCustom((c) => c ?? rateFormFrom(cur));
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [isAdmin]);

  const typeBody = useCallback((): Pick<SubcontractorCreateInput, "kind" | "rateCardSource" | "effectiveFrom" | "customRates" | "technician"> | { error: string } | null => {
    if (!type.kind) return null;
    if (type.kind === "SUPPLY_AND_FIT") return { kind: "SUPPLY_AND_FIT" };
    if (!/^\d{4}-\d{2}-\d{2}$/.test(type.effectiveFrom)) return { error: "Enter the date the rates start." };
    if (type.effectiveFrom < today) return { error: "The rates cannot start in the past. Choose today or a later date." };
    const base = { kind: "LABOUR" as const, rateCardSource: type.rateCardSource, effectiveFrom: type.effectiveFrom, technician: type.technician };
    if (type.rateCardSource === "STANDARD") return base;
    const built = custom ? rateBodyFromForm(custom) : { error: "The standard card could not be read to prefill the custom rates." };
    if ("error" in built) return built;
    return { ...base, customRates: built.body };
  }, [type, custom, today]);

  // The plan line: the server's own words for what the save will do, asked whenever the choice changes.
  useEffect(() => {
    const t = typeBody();
    setPlan(null);
    setPlanErr(null);
    if (!t) return;
    if ("error" in t) {
      setPlanErr(t.error);
      return;
    }
    let live = true;
    api.subcontractors
      .previewCreate({ company: form.company.trim() || "Preview", contactName: "Preview", email: "preview@example.com", trade: "Preview", ...t })
      .then((p) => live && setPlan(p.plan))
      .catch((e) => live && setPlanErr(friendlyApiError(e, "Could not check what this would do. Please try again.")));
    return () => {
      live = false;
    };
    // The company name is not part of the plan; only the type choice is.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeBody]);

  async function save() {
    if (!form.company.trim() || !form.contactName.trim() || !form.email.trim() || !form.trade.trim()) {
      setErr("Company, contact name, email and trade are all required.");
      return;
    }
    const t = typeBody();
    if (!t) {
      setErr("Choose Labour subcontractor or Supply and fit company.");
      return;
    }
    if ("error" in t) {
      setErr(t.error);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const created = await api.subcontractors.create({
        company: form.company.trim(),
        contactName: form.contactName.trim(),
        email: form.email.trim(),
        trade: form.trade.trim(),
        ...(form.contactPhone.trim() ? { contactPhone: form.contactPhone.trim() } : {}),
        ...t,
      });
      onAdded(created.id, { drafted: created.inviteDrafted, error: created.inviteDraftError, expiresAt: created.inviteLinkExpiresAt });
    } catch (e) {
      setErr(friendlyApiError(e, "Could not add the subcontractor."));
      setBusy(false);
    }
  }

  return (
    <ModalShell ariaLabel="Add subcontractor" onClose={() => !busy && onClose()} width="max-w-2xl" margin="my-8" panelClassName="p-6">
        <h2 className="mb-1 font-heading text-2xl tracking-wide text-ele-strong">Add subcontractor</h2>
        <p className="mb-4 text-sm text-ele-body">An invitation is left in Gmail Drafts for you to send. Its secure link (valid for 7 days) lets them complete their own registration and upload their certificates.</p>
        <KindCards value={type.kind} onChange={(k) => setType((t) => ({ ...t, kind: k }))} disabled={busy} />
        {type.kind === "LABOUR" && (
          <>
            <LabourOptions value={type} onChange={setType} isAdmin={isAdmin} today={today} disabled={busy} />
            {isAdmin && type.rateCardSource === "STANDARD" && (
              <div className="mt-4 rounded-ele border border-ele-purple-accent p-3">
                {standard ? (
                  <RateCardTable card={standard} caption={`${standard.label}, in force from ${standard.effectiveFromLabel}`} />
                ) : (
                  <p className="text-sm text-ele-body">Reading the standard card...</p>
                )}
              </div>
            )}
            {isAdmin && type.rateCardSource === "CUSTOM" && custom && (
              <div className="mt-4 rounded-ele border border-ele-purple-accent p-3">
                <p className="mb-2 text-sm text-ele-body">Prefilled from the standard card in force. Leave a line blank for not agreed; an all-in card leaves travel and the other lines blank.</p>
                <RateFiguresFields form={custom} onChange={setCustom} />
              </div>
            )}
          </>
        )}
        {type.kind === "SUPPLY_AND_FIT" && <p className="mt-4 text-sm text-ele-body">{SUPPLY_AND_FIT_NOTE}</p>}

        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <ModalField label="Company" value={form.company} onChange={(v) => set("company", v)} />
          <ModalField label="Contact name" value={form.contactName} onChange={(v) => set("contactName", v)} />
          <ModalField label="Email" value={form.email} onChange={(v) => set("email", v)} type="email" />
          <ModalField label="Trade" value={form.trade} onChange={(v) => set("trade", v)} placeholder="e.g. Lift engineer" />
          <ModalField label="Phone (optional)" value={form.contactPhone} onChange={(v) => set("contactPhone", v)} placeholder="A number you already have for them" />
        </div>
        <p className="mt-2 text-xs text-ele-body">
          A phone number you already have for them is kept, and is the one to use when you phone to confirm their bank
          details before the first payment.
        </p>
        {planErr && <p role="alert" className="mt-3 rounded-ele border border-ele-amber-line bg-ele-surface px-3 py-2 text-sm text-ele-strong">{planErr}</p>}
        {err && <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">{err}</p>}
        <div className={`mt-4 gap-2 ${MODAL_ACTIONS}`}>
          <Button variant="ghost" className={MODAL_ACTION_BTN} onClick={onClose}>Cancel</Button>
          <Button className={MODAL_ACTION_BTN} onClick={save} disabled={busy || !plan}>
            {busy ? "Adding..." : (plan ?? (type.kind ? "Checking..." : "Choose a type"))}
          </Button>
        </div>
    </ModalShell>
  );
}

// Actions for an ACTIVE (non-archived) row: Archive is the normal offboarding (always available);
// Delete is a permanent, less-prominent option shown ONLY for a deletable record (no signed
// agreement and no linked projects, mirroring the server guard). Archive opens the single-step
// archive modal; Delete opens the two-step delete modal.
function RowActions({ sub, onChanged }: { sub: SubcontractorListItem; onChanged: () => void }) {
  const [dialog, setDialog] = useState<null | "archive" | "delete">(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const canDelete = sub.agreementStatus !== "signed" && sub.linkedProjectCount === 0;

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      setDialog(null);
      onChanged();
    } catch (e) {
      // Session AEH B3: this used to swallow the failure silently, leaving the office clicking a
      // button that visibly did nothing. Close the dialog (it sits in a full-screen portal that
      // would otherwise hide this message) and surface why the action did not go through.
      setErr(friendlyApiError(e, "That action could not be completed."));
      setDialog(null);
      setBusy(false);
    }
  }

  return (
    <div onClick={(e) => e.stopPropagation()} className="flex flex-wrap items-center justify-end gap-2">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setDialog("archive")}
        aria-label={`Archive ${sub.company}`}
        title="Archive (stop working with this subcontractor)"
      >
        Archive
      </Button>
      {canDelete && (
        <Button
          variant="destructive"
          size="sm"
          onClick={() => setDialog("delete")}
          aria-label={`Delete ${sub.company}`}
          title="Delete (only for a record created by mistake)"
        >
          Delete
        </Button>
      )}
      {err && (
        <span role="alert" className="w-full text-right text-[11px] text-ele-danger" style={{ whiteSpace: "normal" }}>
          {err}
        </span>
      )}
      {dialog === "archive" && (
        <SubcontractorArchiveDialog busy={busy} onConfirm={() => run(() => api.subcontractors.archive(sub.id))} onCancel={() => setDialog(null)} />
      )}
      {dialog === "delete" && (
        <SubcontractorDeleteDialog busy={busy} onConfirm={() => run(() => api.subcontractors.remove(sub.id))} onCancel={() => setDialog(null)} />
      )}
    </div>
  );
}

// Restore an archived subcontractor back to the active list. A reversible, low-stakes action, so
// it needs no confirmation.
function RowRestoreButton({ sub, onRestored }: { sub: SubcontractorListItem; onRestored: () => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function doRestore() {
    setBusy(true);
    setErr(null);
    try {
      await api.subcontractors.restore(sub.id);
      onRestored();
    } catch (e) {
      setErr(friendlyApiError(e, "Could not restore the subcontractor."));
      setBusy(false);
    }
  }
  return (
    <span className="flex flex-wrap items-center justify-end gap-2">
      <Button
        variant="secondary"
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          doRestore();
        }}
        disabled={busy}
        aria-label={`Restore ${sub.company}`}
        title="Restore to the active list"
      >
        {busy ? "..." : "Restore"}
      </Button>
      {err && (
        <span role="alert" className="w-full text-right text-[11px] text-ele-danger" style={{ whiteSpace: "normal" }}>
          {err}
        </span>
      )}
    </span>
  );
}

function ModalField({ label, value, onChange, type = "text", placeholder }: { label: string; value: string; onChange: (v: string) => void; type?: string; placeholder?: string }) {
  return (
    <div>
      <label className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-blue">{label}</label>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        aria-label={label}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong"
      />
    </div>
  );
}
